import { useEffect, useState, type FormEvent } from 'react';
import { Navigate } from 'react-router';
import {
  Box, Typography, Paper, Stack, Button, TextField, MenuItem, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, Switch, Divider,
  Tooltip, Snackbar,
} from '@mui/material';
import {
  AddOutlined, EditOutlined, SendOutlined, CheckCircleOutlined,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { AdminUser } from '@lorrainemarwood-care/shared';
import { api } from '../lib/api';
import { useAuth } from '../hooks/useAuth';

dayjs.extend(relativeTime);

type Role = 'family' | 'nurse' | 'admin';

const ROLE_LABELS: Record<Role, string> = {
  family: 'Family',
  nurse: 'Nurse',
  admin: 'Admin',
};

const ROLE_COLOR: Record<Role, 'default' | 'primary' | 'secondary'> = {
  family: 'default',
  nurse: 'secondary',
  admin: 'primary',
};

export default function Admin() {
  const { user } = useAuth();
  const [editing, setEditing] = useState<AdminUser | 'new' | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api.get<AdminUser[]>('/api/admin/users'),
    enabled: user?.role === 'admin',
  });

  const magicLinkMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/users/${id}/magic-link`),
    onSuccess: (_data, id) => {
      const target = (query.data ?? []).find((u) => u.id === id);
      setToast(`Magic link sent to ${target?.email ?? 'user'}`);
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, disabled }: { id: string; disabled: boolean }) =>
      api.patch<AdminUser>(`/api/admin/users/${id}`, { disabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
    onError: (err) => setToast((err as Error).message),
  });

  if (!user) return null;
  if (user.role !== 'admin') return <Navigate to="/" replace />;

  const users = query.data ?? [];
  const active = users.filter((u) => !u.disabled);
  const disabled = users.filter((u) => u.disabled);

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>Admin · Users</Typography>
        <Button
          variant="contained"
          startIcon={<AddOutlined />}
          onClick={() => setEditing('new')}
        >
          Add user
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 3 }}>
        Adding a user here puts their email on the allow-list and creates their account.
        They can sign in via a magic link (send one with the paper-plane button below).
        Disabling revokes their active sessions immediately.
      </Alert>

      <Stack spacing={1.5}>
        {users.length === 0 && !query.isLoading && (
          <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
            No users yet.
          </Box>
        )}
        {active.map((u) => (
          <UserRow
            key={u.id}
            u={u}
            self={u.id === user.id}
            onEdit={() => setEditing(u)}
            onSendLink={() => magicLinkMutation.mutate(u.id)}
            sendingLink={magicLinkMutation.isPending && magicLinkMutation.variables === u.id}
            onToggleDisabled={(v) => toggleMutation.mutate({ id: u.id, disabled: v })}
          />
        ))}

        {disabled.length > 0 && (
          <>
            <Divider sx={{ my: 2 }}>
              <Chip label="Disabled" size="small" />
            </Divider>
            {disabled.map((u) => (
              <UserRow
                key={u.id}
                u={u}
                self={u.id === user.id}
                onEdit={() => setEditing(u)}
                onSendLink={() => magicLinkMutation.mutate(u.id)}
                sendingLink={magicLinkMutation.isPending && magicLinkMutation.variables === u.id}
                onToggleDisabled={(v) => toggleMutation.mutate({ id: u.id, disabled: v })}
              />
            ))}
          </>
        )}
      </Stack>

      <UserDialog
        open={editing !== null}
        initial={editing === 'new' ? null : (editing ?? null)}
        onClose={() => setEditing(null)}
      />

      <Snackbar
        open={toast !== null}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        message={toast}
      />
    </Box>
  );
}

function UserRow({
  u, self, onEdit, onSendLink, sendingLink, onToggleDisabled,
}: {
  u: AdminUser;
  self: boolean;
  onEdit: () => void;
  onSendLink: () => void;
  sendingLink: boolean;
  onToggleDisabled: (disabled: boolean) => void;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2, opacity: u.disabled ? 0.55 : 1 }}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {u.name}
            </Typography>
            <Chip
              size="small"
              label={ROLE_LABELS[u.role]}
              color={ROLE_COLOR[u.role]}
              variant="outlined"
            />
            {self && <Chip size="small" label="you" variant="outlined" />}
            {u.telegramLinked && (
              <Chip
                size="small"
                icon={<CheckCircleOutlined />}
                label={u.telegramUsername ? `TG @${u.telegramUsername}` : 'Telegram linked'}
                color="success"
                variant="outlined"
              />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {u.email}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            added {dayjs(u.createdAt).fromNow()}
            {u.lastSeenAt ? ` · last seen ${dayjs(u.lastSeenAt).fromNow()}` : ' · never signed in'}
          </Typography>
        </Box>

        <Tooltip title="Send magic-link email">
          <span>
            <IconButton onClick={onSendLink} disabled={sendingLink || u.disabled}>
              <SendOutlined />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={u.disabled ? 'Re-enable' : 'Disable'}>
          <span>
            <Switch
              checked={!u.disabled}
              onChange={(_, checked) => onToggleDisabled(!checked)}
              disabled={self}
            />
          </span>
        </Tooltip>

        <IconButton onClick={onEdit}>
          <EditOutlined />
        </IconButton>
      </Stack>
    </Paper>
  );
}

function UserDialog({
  open, initial, onClose,
}: {
  open: boolean;
  initial: AdminUser | null;
  onClose: () => void;
}) {
  const isNew = initial === null;
  const [email, setEmail] = useState(initial?.email ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [role, setRole] = useState<Role>(initial?.role ?? 'family');
  const qc = useQueryClient();

  // Re-sync local state when the dialog opens for a different user (or switches
  // between add/edit mode).
  useEffect(() => {
    if (!open) return;
    setEmail(initial?.email ?? '');
    setName(initial?.name ?? '');
    setRole(initial?.role ?? 'family');
  }, [open, initial]);

  const createMutation = useMutation({
    mutationFn: () => api.post<AdminUser>('/api/admin/users', { email: email.trim(), name: name.trim(), role }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      setEmail(''); setName(''); setRole('family');
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: () => api.patch<AdminUser>(`/api/admin/users/${initial!.id}`, { name: name.trim(), role }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      onClose();
    },
  });

  const mutation = isNew ? createMutation : updateMutation;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    if (isNew && !email.trim()) return;
    mutation.mutate();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{isNew ? 'Add user' : 'Edit user'}</DialogTitle>
      <Box component="form" onSubmit={onSubmit}>
        <DialogContent>
          <Stack spacing={2}>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={!isNew}
              size="small"
              autoFocus={isNew}
            />
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              size="small"
              autoFocus={!isNew}
            />
            <TextField
              select
              label="Role"
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              size="small"
            >
              <MenuItem value="family">Family</MenuItem>
              <MenuItem value="nurse">Nurse</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </TextField>
            {mutation.isError && (
              <Alert severity="error">{(mutation.error as Error).message}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={mutation.isPending || !name.trim() || (isNew && !email.trim())}
          >
            {mutation.isPending ? 'Saving…' : (isNew ? 'Add' : 'Save')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
